import { useEffect, useState } from 'react';

export interface INavigationRoute {
  id: string;
  path: string;
  label: string;
  description?: string;
  keywords?: string[];
}

export function usePrimaryNavRoutes(): {
  routes: INavigationRoute[];
} {
  const [routes, setRoutes] = useState<INavigationRoute[]>([]);

  useEffect(() => {
    const parseElementToRoute = (element: Element): INavigationRoute | null => {
      const htmlElement = element as HTMLElement;
      const anchor = element.querySelector('a') || (element as HTMLAnchorElement);

      if (!anchor?.href) {
        return null;
      }

      // Extract path from href (remove domain part)
      const url = new URL(anchor.href);
      const path = url.pathname;
      // Get attributes with fallbacks
      const label = htmlElement.dataset.commandLabel || anchor.textContent?.trim() || '';
      const description = htmlElement.dataset.commandDescription;
      const keywordsStr = htmlElement.dataset.commandKeywords;
      const keywords = keywordsStr ? keywordsStr.split(',').map((k) => k.trim()) : [];

      // Generate id from path
      const id = path.replace(/^\//, '').replaceAll('/', '-') || 'root';

      if (!label || !path) {
        return null;
      }

      return {
        id,
        path,
        label,
        description,
        keywords,
      };
    };

    const parseNavRoutes = (): INavigationRoute[] => {
      const primaryNav = document.getElementById('primary-nav');
      if (!primaryNav) {
        return [];
      }

      const commandElements = primaryNav.querySelectorAll('[data-command="true"]');
      return Array.from(commandElements)
        .map(parseElementToRoute)
        .filter((route): route is INavigationRoute => route !== null);
    };

    // Initial parse
    setRoutes(parseNavRoutes());

    // Re-parse when DOM changes (e.g., permissions change, nav updates)
    const observer = new MutationObserver(() => {
      setRoutes(parseNavRoutes());
    });

    const primaryNav = document.getElementById('primary-nav');
    if (primaryNav) {
      observer.observe(primaryNav, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: [
          'data-command',
          'data-command-label',
          'data-command-description',
          'data-command-keywords',
          // Also watch for href changes
          'href',
        ],
      });
    }

    // Also listen for URL changes which might trigger navigation updates
    let timeoutId: NodeJS.Timeout;
    const handleUrlChange = (): void => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        const newRoutes = parseNavRoutes();
        setRoutes(newRoutes);
      }, 200);
    };

    globalThis.addEventListener('popstate', handleUrlChange);

    return () => {
      observer.disconnect();
      globalThis.removeEventListener('popstate', handleUrlChange);
      clearTimeout(timeoutId);
    };
  }, []);

  return { routes };
}
